import { createContext, useContext } from 'react';
import { Project } from '../../services/projects';

/**
 * Shared state for every route under /projects/:id.
 *
 * The layout owns loading, polling and the project object; the sub-routes read
 * them from here. `project` is non-null by construction: the layout renders the
 * Outlet only once the project has loaded, so pages never guard against null.
 */
export interface ProjectContextValue {
  project: Project;
  /** Re-fetch the project. `silent` skips the loading state (used by polling). */
  reload: (silent?: boolean) => Promise<void>;

  /** Celery task of the running analysis, for granular progress. */
  currentTaskId: string | null;
  setCurrentTaskId: (taskId: string | null) => void;
  analysisProgress: number;

  /**
   * Client-side ADOSA recalculation. It lives here rather than in the ADOSA page
   * because the KPI strips outside that page read the recalculated figures too;
   * keeping one owner is what stops the two from drifting apart.
   *
   * These values do NOT come from the audited pipeline and carry no provenance.
   */
  adosaOverride: Record<string, any> | null;
  setAdosaOverride: (value: Record<string, any> | null) => void;

  /** Airport centre from analysis_results, falling back to the AIP AD2 coords. */
  airportCenter: { lat: number; lon: number } | null;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

export const ProjectProvider = ProjectContext.Provider;

export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) {
    throw new Error('useProject must be used inside a /projects/:id route');
  }
  return ctx;
}
